import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAvailability } from '../hooks/useAvailability';
import { createBooking, blockSpot, calcolaPrezzo, cancelBookingAndRelease, getOrdersByBeachAndDate } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelHead, PanelBody, Spinner, Button } from '../components/ui';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const WEEKDAYS_FULL = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const STATUS_STYLE = {
  free: { bg: '#14453D', border: 'rgba(31,147,224,0.5)' },
  occupied: { bg: '#902D41', border: 'rgba(231,76,60,0.5)' },
  reserved: { bg: 'rgba(245,200,66,0.2)', border: 'rgba(245,200,66,0.5)' },
  blocked: { bg: 'rgba(120,120,120,0.2)', border: 'rgba(120,120,120,0.5)' },
};

const DATE_TYPES = [
  { id: 'single', label: 'Giorno singolo', icon: '📅' },
  { id: 'range', label: 'Periodo continuativo', icon: '📆' },
  { id: 'multiple', label: 'Date non consecutive', icon: '🗓️' },
  { id: 'seasonal', label: 'Stagionale (giorni sett.)', icon: '🌊' },
];

export default function BeachMapPage() {
  const { admin, beach, tariffe } = useAuth();
  const today = new Date().toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const { availabilityMap, loading, getStatsForDate, refetch } = useAvailability(admin?.beachCode);

  const [drawerCell, setDrawerCell] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [relatedOrder, setRelatedOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const [form, setForm] = useState({
    guestName: '', guestPhone: '', notes: '',
    dateType: 'single',
    dateRange: { dal: today, al: today },
    dates: [], seasonalDays: [], totalPrice: '',
  });

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const daysInMonth = useMemo(() => {
    const { year, month } = viewMonth;
    const count = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return { dateStr: d.toISOString().split('T')[0], day: i + 1, weekday: WEEKDAYS[d.getDay()] };
    });
  }, [viewMonth]);

  const prevMonth = () => setViewMonth(({ year, month }) =>
    month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 });
  const nextMonth = () => setViewMonth(({ year, month }) =>
    month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 });

  const layoutDoc = availabilityMap[selectedDate];
  const layoutCelle = layoutDoc?.layout || [];
  const servizi = beach?.servizi || [];

  const rows = useMemo(() => {
    const map = {};
    layoutCelle.forEach(cell => {
      if (!map[cell.row]) map[cell.row] = [];
      map[cell.row].push(cell);
    });
    return Object.keys(map).map(Number).sort((a, b) => a - b).map(rowIdx => ({
      rowIdx, label: LETTERS[rowIdx],
      cells: map[rowIdx].sort((a, b) => a.col - b.col),
    }));
  }, [layoutCelle]);

  const stats = getStatsForDate(selectedDate);

  const prezzoSuggerito = useMemo(() => {
    if (!drawerCell || !tariffe?.length) return null;
    const s = servizi.find(s => s.id === drawerCell.type);
    if (!s) return null;
    return calcolaPrezzo(tariffe, selectedDate, drawerCell.type, drawerCell.row, s.haFila);
  }, [drawerCell, tariffe, selectedDate, servizi]);

  // Carica ordine associato quando apriamo in modalità info
  useEffect(() => {
    if (drawerMode !== 'info' || !drawerCell || !admin?.beachCode) return;
    setRelatedOrder(null);
    setLoadingOrder(true);
    getOrdersByBeachAndDate(admin.beachCode, selectedDate)
      .then(orders => {
        const found = orders.find(o =>
          o.status !== 'cancelled' &&
          o.celle?.some(c => c.id === drawerCell.id)
        );
        setRelatedOrder(found || null);
      })
      .catch(() => setRelatedOrder(null))
      .finally(() => setLoadingOrder(false));
  }, [drawerMode, drawerCell, selectedDate, admin?.beachCode]);

  const openDrawer = (cell) => {
    setDrawerCell(cell);
    setDrawerMode(cell.status === 'free' ? 'book' : 'info');
    setRelatedOrder(null);
    setForm(p => ({
      ...p, dateType: 'single',
      dateRange: { dal: selectedDate, al: selectedDate },
      dates: [selectedDate], totalPrice: '',
      guestName: '', guestPhone: '', notes: '', seasonalDays: [],
    }));
  };

  const closeDrawer = () => { setDrawerCell(null); setDrawerMode(null); setRelatedOrder(null); };

  const handleSave = async () => {
    if (!drawerCell) return;
    setSaving(true);
    try {
      const params = {
        celle: [drawerCell],
        guestName: form.guestName, guestPhone: form.guestPhone, notes: form.notes,
        dateType: form.dateType,
        selectedDate: form.dateType === 'single' ? selectedDate : undefined,
        dateRange: ['range', 'seasonal'].includes(form.dateType) ? form.dateRange : undefined,
        dates: form.dateType === 'multiple' ? form.dates : undefined,
        seasonalDays: form.dateType === 'seasonal' ? form.seasonalDays : undefined,
        totalPrice: Number(form.totalPrice) || 0,
      };
      if (drawerMode === 'block') await blockSpot(admin.beachCode, params);
      else await createBooking(admin.beachCode, params);
      await refetch();
      closeDrawer();
    } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!relatedOrder) return;
    if (!window.confirm('Sei sicuro di voler cancellare questa prenotazione?')) return;
    setCancelling(true);
    try {
      await cancelBookingAndRelease(admin.beachCode, relatedOrder.id, relatedOrder.celle, relatedOrder.selectedDate);
      await refetch();
      closeDrawer();
    } finally { setCancelling(false); }
  };

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
  };

  const servizio = drawerCell ? servizi.find(s => s.id === drawerCell?.type) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar title="Mappa Spiaggia" subtitle={`${Object.keys(availabilityMap).length} date configurate`} />

      <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>

        {/* Navigazione mese */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <button onClick={prevMonth} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-soft)', padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: '13px' }}>←</button>
            <div style={{ fontSize: '14px', fontWeight: 500, minWidth: '160px', textAlign: 'center' }}>{MONTHS[viewMonth.month]} {viewMonth.year}</div>
            <button onClick={nextMonth} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-soft)', padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: '13px' }}>→</button>
          </div>
          <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '4px' }}>
            {daysInMonth.map(({ dateStr, day, weekday }) => {
              const isActive = dateStr === selectedDate;
              const isToday = dateStr === today;
              const hasLayout = !!availabilityMap[dateStr];
              const s = getStatsForDate(dateStr);
              const pct = s.total > 0 ? Math.round((s.occupied / s.total) * 100) : 0;
              return (
                <div key={dateStr} onClick={() => setSelectedDate(dateStr)} style={{
                  flexShrink: 0, width: '44px', padding: '7px 4px', borderRadius: 'var(--radius-md)',
                  textAlign: 'center', cursor: 'pointer',
                  border: `1px solid ${isActive ? 'var(--ocean)' : isToday ? 'rgba(245,200,66,0.4)' : 'var(--border)'}`,
                  background: isActive ? 'var(--ocean)' : 'var(--surface)',
                  opacity: hasLayout ? 1 : 0.4, transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: '9px', color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)', textTransform: 'uppercase' }}>{weekday}</div>
                  <div style={{ fontSize: '15px', fontWeight: 500, marginTop: '2px' }}>{day}</div>
                  {hasLayout && pct > 0 && (
                    <div style={{ height: '3px', borderRadius: '2px', marginTop: '4px', background: pct > 80 ? '#f87171' : pct > 50 ? 'var(--sand)' : '#4ade80', width: `${pct}%`, margin: '4px auto 0' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '60px' }}><Spinner size={32} /></div>
        ) : layoutCelle.length === 0 ? (
          <Panel><PanelBody>
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
              🏖️ Nessun layout per questa data.<br />
              <span style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                Pubblica il layout dal <strong style={{ color: 'var(--text-soft)' }}>Beach Builder</strong>.
              </span>
            </div>
          </PanelBody></Panel>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '16px' }}>
            <Panel>
              <PanelHead title="Layout spiaggia" subtitle={`${selectedDate} · clicca un posto per gestirlo`} />
              <PanelBody>
                <div style={{ height: '32px', background: 'rgba(31,147,224,0.12)', borderRadius: '8px 8px 0 0', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'rgba(31,147,224,0.6)', letterSpacing: '0.06em' }}>〰 Mare 〰</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {rows.map(({ rowIdx, label, cells }) => (
                    <div key={rowIdx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '20px', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', flexShrink: 0 }}>{label}</div>
                      {cells.map(cell => {
                        const srv = servizi.find(s => s.id === cell.type);
                        const st = STATUS_STYLE[cell.status] || STATUS_STYLE.free;
                        const isSelected = drawerCell?.id === cell.id;
                        return (
                          <div key={cell.id} onClick={() => openDrawer(cell)}
                            title={`${cell.id} · ${srv?.label || cell.type} · ${cell.status}`}
                            style={{
                              width: '32px', height: '32px', borderRadius: '6px',
                              background: isSelected ? 'var(--sand)' : st.bg,
                              border: `${isSelected ? '2px' : '1px'} solid ${isSelected ? 'var(--sand)' : st.border}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', fontSize: '13px', flexShrink: 0, transition: 'transform 0.1s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            {srv?.icona || '?'}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div style={{ height: '20px', background: 'rgba(245,200,66,0.08)', borderRadius: '0 0 8px 8px', marginTop: '8px' }} />
                <div style={{ display: 'flex', gap: '14px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {Object.entries({ free: `Libero (${stats.free})`, occupied: `Occupato (${stats.occupied})`, reserved: 'Prenotato', blocked: 'Bloccato' }).map(([status, label]) => (
                    <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: STATUS_STYLE[status]?.border }} />{label}
                    </div>
                  ))}
                </div>
              </PanelBody>
            </Panel>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Panel>
                <PanelHead title="Riepilogo" subtitle={selectedDate} />
                <PanelBody style={{ padding: '12px 20px' }}>
                  {[
                    { label: 'Totale posti', value: stats.total, color: 'var(--text-primary)' },
                    { label: 'Liberi', value: stats.free, color: '#4ade80' },
                    { label: 'Occupati', value: stats.occupied, color: '#f87171' },
                    { label: 'Occupazione', value: stats.total > 0 ? `${Math.round((stats.occupied / stats.total) * 100)}%` : '—', color: 'var(--sand)' },
                  ].map(row => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                      <span style={{ fontWeight: 500, color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                </PanelBody>
              </Panel>
              <Panel>
                <PanelHead title="Occupazione mensile" />
                <PanelBody style={{ padding: '12px 20px' }}>
                  {daysInMonth.filter(d => availabilityMap[d.dateStr]).slice(0, 12).map(d => {
                    const s = getStatsForDate(d.dateStr);
                    const pct = s.total > 0 ? (s.occupied / s.total) * 100 : 0;
                    return (
                      <div key={d.dateStr} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '24px', flexShrink: 0 }}>{d.day}</span>
                        <div style={{ flex: 1, height: '5px', background: 'var(--surface)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '3px', width: `${pct}%`, background: pct > 80 ? '#f87171' : 'var(--ocean)' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '28px', textAlign: 'right' }}>{Math.round(pct)}%</span>
                      </div>
                    );
                  })}
                </PanelBody>
              </Panel>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      {drawerCell && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end' }} onClick={closeDrawer}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '400px', height: '100%', background: 'var(--navy-mid)',
            borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
          }}>

            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>{servizio?.icona} {servizio?.label} {drawerCell.id}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {selectedDate} · {
                      drawerCell.status === 'free' ? 'Disponibile' :
                        drawerCell.status === 'occupied' ? 'Prenotato' :
                          drawerCell.status === 'blocked' ? 'Bloccato' : 'Prenotato'
                    }
                  </div>
                </div>
                <button onClick={closeDrawer} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px' }}>×</button>
              </div>
              {drawerCell.status === 'free' && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <ModeButton id="book" label="📋 Prenota" drawerMode={drawerMode} setDrawerMode={setDrawerMode} />
                  <ModeButton id="block" label="🔒 Blocca" drawerMode={drawerMode} setDrawerMode={setDrawerMode} />
                </div>
              )}
            </div>

            {/* ── INFO: dati prenotazione esistente ── */}
            {drawerMode === 'info' && (
              <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>
                {loadingOrder ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}><Spinner size={24} /></div>
                ) : relatedOrder ? (
                  <>
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                        Dettagli prenotazione
                      </div>
                      {[
                        ['Cliente', relatedOrder.guestName || '—'],
                        ['Telefono', relatedOrder.guestPhone || '—'],
                        ['Totale', relatedOrder.totalPrice ? `€ ${relatedOrder.totalPrice}` : '—'],
                        ['Date', relatedOrder.selectedDate?.slice(0, 3).join(', ') + (relatedOrder.selectedDate?.length > 3 ? ` +${relatedOrder.selectedDate.length - 3}` : '') || '—'],
                        ['Note', relatedOrder.notes || '—'],
                        ['ID ordine', relatedOrder.id?.slice(0, 10) + '…'],
                      ].map(([label, value]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '13px', gap: '12px' }}>
                          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                          <span style={{ color: 'var(--text-soft)', fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
                        </div>
                      ))}
                    </div>

                    <button onClick={handleCancel} disabled={cancelling} style={{
                      width: '100%', padding: '10px', borderRadius: 'var(--radius-md)',
                      background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.35)',
                      color: '#f87171', fontSize: '13px', cursor: cancelling ? 'default' : 'pointer',
                      fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '6px', opacity: cancelling ? 0.6 : 1,
                    }}>
                      {cancelling ? <Spinner size={14} /> : '🗑 Cancella prenotazione'}
                    </button>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '14px', background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', lineHeight: 1.6 }}>
                    Posto segnato come <strong style={{ color: 'var(--text-soft)' }}>{drawerCell.status}</strong> ma nessun ordine trovato per questa data.<br />
                    Potrebbe essere stato prenotato dall'app mobile o da un altro canale.
                  </div>
                )}
              </div>
            )}

            {/* ── FORM: prenota / blocca ── */}
            {(drawerMode === 'book' || drawerMode === 'block') && (
              <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Tipo di prenotazione
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {DATE_TYPES.map(dt => (
                      <button key={dt.id} onClick={() => setForm(p => ({ ...p, dateType: dt.id }))} style={{
                        padding: '8px 10px', borderRadius: 'var(--radius-md)', fontSize: '12px',
                        border: `1px solid ${form.dateType === dt.id ? 'var(--ocean)' : 'var(--border)'}`,
                        background: form.dateType === dt.id ? 'rgba(31,147,224,0.12)' : 'var(--surface)',
                        color: form.dateType === dt.id ? 'var(--ocean)' : 'var(--text-soft)',
                        cursor: 'pointer', fontFamily: 'var(--font-sans)', textAlign: 'left',
                      }}>
                        {dt.icon} {dt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {form.dateType === 'single' && (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Data</label>
                    <div style={{ ...inputStyle, padding: '9px 12px', color: 'var(--text-soft)' }}>{selectedDate}</div>
                  </div>
                )}

                {(form.dateType === 'range' || form.dateType === 'seasonal') && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {[['Dal', 'dal'], ['Al', 'al']].map(([lbl, key]) => (
                      <div key={key}>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{lbl}</label>
                        <input type="date" value={form.dateRange[key]}
                          onChange={e => setForm(p => ({ ...p, dateRange: { ...p.dateRange, [key]: e.target.value } }))}
                          style={inputStyle} />
                      </div>
                    ))}
                  </div>
                )}

                {form.dateType === 'seasonal' && (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Giorni della settimana
                    </label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {WEEKDAYS_FULL.map((wd, i) => {
                        const active = form.seasonalDays.includes(i);
                        return (
                          <button key={i} onClick={() => setForm(p => ({ ...p, seasonalDays: active ? p.seasonalDays.filter(d => d !== i) : [...p.seasonalDays, i] }))} style={{
                            padding: '5px 10px', borderRadius: '20px', fontSize: '11.5px',
                            border: `1px solid ${active ? 'var(--ocean)' : 'var(--border)'}`,
                            background: active ? 'rgba(31,147,224,0.15)' : 'transparent',
                            color: active ? 'var(--ocean)' : 'var(--text-muted)',
                            cursor: 'pointer', fontFamily: 'var(--font-sans)',
                          }}>{wd}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {form.dateType === 'multiple' && (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Aggiungi date</label>
                    <input type="date" onChange={e => {
                      const d = e.target.value;
                      if (d && !form.dates.includes(d)) setForm(p => ({ ...p, dates: [...p.dates, d].sort() }));
                      e.target.value = '';
                    }} style={inputStyle} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' }}>
                      {form.dates.map(d => (
                        <div key={d} style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px', background: 'rgba(31,147,224,0.15)', border: '1px solid rgba(31,147,224,0.3)', color: 'var(--ocean)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {d}
                          <span onClick={() => setForm(p => ({ ...p, dates: p.dates.filter(x => x !== d) }))} style={{ cursor: 'pointer', fontSize: '13px', lineHeight: 1 }}>×</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drawerMode === 'book' && (
                  <>
                    <div style={{ height: '1px', background: 'var(--border)' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome cliente</label>
                        <input placeholder="Mario Rossi" value={form.guestName} onChange={setF('guestName')} style={inputStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Telefono (opzionale)</label>
                        <input placeholder="+39 333 1234567" value={form.guestPhone} onChange={setF('guestPhone')} style={inputStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                          Prezzo totale {prezzoSuggerito && <span style={{ color: 'var(--ocean)' }}>(suggerito: € {prezzoSuggerito})</span>}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--sand)', fontSize: '13px' }}>€</span>
                          <input type="number" min="0" step="0.5" placeholder={prezzoSuggerito || '0'}
                            value={form.totalPrice} onChange={setF('totalPrice')}
                            style={{ ...inputStyle, paddingLeft: '24px' }} />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                    {drawerMode === 'block' ? 'Motivo blocco (opzionale)' : 'Note (opzionale)'}
                  </label>
                  <textarea rows={2}
                    placeholder={drawerMode === 'block' ? 'es. Manutenzione, riservato VIP...' : 'es. Cliente abituale, allergie...'}
                    value={form.notes} onChange={setF('notes')}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} />
                </div>
              </div>
            )}

            {/* Footer */}
            {(drawerMode === 'book' || drawerMode === 'block') && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', flexShrink: 0 }}>
                <Button variant={drawerMode === 'block' ? 'ghost' : 'primary'} onClick={handleSave} disabled={saving} fullWidth
                  style={drawerMode === 'block' ? { background: 'rgba(120,120,120,0.2)', borderColor: 'rgba(120,120,120,0.4)', color: '#fff' } : {}}>
                  {saving ? <Spinner size={14} /> : drawerMode === 'block' ? '🔒 Blocca posto' : '✓ Conferma prenotazione'}
                </Button>
                <Button variant="ghost" onClick={closeDrawer}>Annulla</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeButton({ id, label, drawerMode, setDrawerMode }) {
  const active = drawerMode === id;
  return (
    <button onClick={() => setDrawerMode(active ? null : id)} style={{
      padding: '6px 14px', borderRadius: 'var(--radius-md)', fontSize: '12.5px',
      border: `1px solid ${active ? 'var(--ocean)' : 'var(--border-mid)'}`,
      background: active ? 'rgba(31,147,224,0.15)' : 'transparent',
      color: active ? 'var(--ocean)' : 'var(--text-soft)',
      cursor: 'pointer', fontFamily: 'var(--font-sans)',
    }}>
      {label}
    </button>
  );
}